import { Box, IconButton, Tooltip } from "@mui/material";
import BittomExpandIcon from "../../assets/common/bottom-expand-ion.svg";
import { SCORE_OPTIONS } from "../../utils/types";
import {
  getScoreSentimentFromFraction,
  SCORE_API_TO_UI_MAP,
} from "../../utils/utils";
import { useEffect, useState } from "react";
import EllipsedTooltip from "../Common/EllipsedTooltip";

const ExpandableHeader = ({
  title,
  isExpanded,
  onExpand,
  onCollapse,
  disabled = false,
  forceHover = false,
}: {
  title: string;
  isExpanded: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  disabled?: boolean;
  forceHover?: boolean;
}) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      gap={0.5}
      sx={{ cursor: disabled ? "default" : "pointer" }}
    >
      <span style={{fontSize: "12px", fontWeight: "500"}}>{title}</span>

      <Tooltip
        open={forceHover}
        title={disabled ? "No questions" : isExpanded ? "Collapse" : "Expand"}
        placement="top"
        arrow
        disableHoverListener
        disableFocusListener
        disableTouchListener
      >
        {/* span required for disabled button */}
        <span>
          <IconButton
            size="small"
            onClick={isExpanded ? onCollapse : onExpand}
            disabled={disabled}
          >
            <img
              src={BittomExpandIcon}
              alt=""
              style={{
                height: "16px",
                width: "16px",
                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
                opacity: disabled ? 0.4 : 1,
              }}
            />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

const headerHoverSx = {
  cursor: "pointer",
  transition: "background-color 0.15s ease",
  backgroundClip: "padding-box", // prevents covering border
  borderTop: "1px solid #C2C2CA",
  borderBottom: "1px solid #7A7A8C",

  borderLeft: "none",
  borderRight: "1px solid #C2C2CA",
  // fake borders slightly inset
  boxShadow:
    "inset 0.1px 0 0 rgba(224,224,224,1), inset -1px 0 0 rgba(224,224,224,1)",

  "&:hover": {
    backgroundColor: "#F2F2F4",

    // keep borders visible on hover too
    boxShadow:
      "inset 0.1px 0 0 rgba(224,224,224,1), inset -1px 0 0 rgba(224,224,224,1)",
  },
};
/* ---------------- Columns Builder ---------------- */

const getColumns = (
  expandedSection: string | null,
  setExpandedSection: (v: string | null) => void,
  sectionDefinitions: Array<{ key: string; label: string }>,
  questionSchema: Record<string, string[]>,
  columnVisibility?: Record<string, boolean>,
  onInteractionTitleClick?: (row: any) => void
) => {
  const isExpanded = expandedSection !== null;
  const [headerHover, setHeaderHover] = useState<string | null>(null);

  useEffect(() => {
    if (expandedSection && (questionSchema[expandedSection]?.length ?? 0) === 0) {
      setExpandedSection(null);
    }
  }, [questionSchema, expandedSection, setExpandedSection]);

  const baseColumns: any[] = [
    {
      id: "title",
      accessorKey: "title",
      header: "Interaction Title",
      size: 300,
      grow: true,
      enableColumnFilter: false,
      enableSorting: false,

      Cell: ({ row }: any) => {
        const title = row.original.title;
        const startTime = row.original.startTime;
        const endTime = row.original.endTime;

        const formatRange = (start: string, end: string) => {
          const startDate = new Date(start);
          const endDate = new Date(end);

          const datePart = startDate.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
          });

          const timeFormatter = new Intl.DateTimeFormat("en-US", {
            hour: "numeric",
            minute: "2-digit",
          });

          return `${datePart}, ${timeFormatter.format(startDate)}–${timeFormatter.format(endDate)}`;
        };

        const showOnlyTitle = expandedSection !== null; // 👈 when toggled

        return (
          <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              maxWidth: "100%",
            }}
          >
            <EllipsedTooltip tooltipContent={title} maxWidth="300px">
              <span
                style={{
                  fontWeight: 400,
                  fontSize: "13px",
                  cursor: onInteractionTitleClick ? "pointer" : "default",
                  color: onInteractionTitleClick ? "#6036D6" : "inherit",
                }}
                onClick={() => onInteractionTitleClick?.(row.original)}
              >
                {title}
              </span>
            </EllipsedTooltip>

            {!showOnlyTitle && startTime && endTime && (
              <>
                <span>|</span>

                <span style={{ fontSize: "12px", color: "#9595A3" }}>
                  {formatRange(startTime, endTime)}
                </span>
              </>
            )}
          </Box>
        );
      },
    },
    {
      id: "score",
      accessorKey: "qualityScore",
      header: "Rating",
      size: 120,
      grow: true,
      enableColumnFilter: false,
      enableSorting: false,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }: any) => {
        const value = cell.getValue();
        if (!value || value === "not_rated") {
          return <span>-</span>;
        }
        const uiKey = SCORE_API_TO_UI_MAP[value];
        const option = SCORE_OPTIONS.find((o) => o.key === uiKey);

        if (!option) return <span>-</span>;

        return (
          <Box
            sx={{
              width: "14px",
              height: "14px",
              borderRadius: "3px",
              backgroundColor: option.color,
              display: "inline-block",
            }}
          />
        );
      },
    },
  ];

  const ScoreCircle = ({ color }: { color: string }) => (
    <Box
      sx={{
        width: 14,
        height: 14,
        borderRadius: "50%",
        backgroundColor: color,
        display: "inline-block",
      }}
    />
  );
  const buildQuestionColumns = (
    questions: string[],
    accessor: string
  ) =>
    questions.map((q, idx) => ({
      id: `${accessor}_${idx}`,
      header: q,
      accessorFn: (row: any) => row[accessor]?.[idx] ?? "-",
      enableSorting: false,
      enableColumnFilter: false,
      muiTableHeadCellProps: { align: "center", sx: {
        fontSize: "10px",
        fontWeight: 500,
        lineHeight: 1.2,
        whiteSpace: "normal",
        textAlign: "center",
        px: 0.5,
        borderBottom: "1px solid #7A7A8C",
        borderLeft: "1px solid #C2C2CA",
      }, },
      muiTableBodyCellProps: { align: "center", sx: {
        borderLeft: "1px solid #C2C2CA",
      } },
      size: 220,

      Cell: ({ cell }: any) => {
        const value = cell.getValue();

        if (!value || value === "-") return <span>-</span>;

        const normalized = String(value).toLowerCase();

        if (normalized === "yes") {
          return <ScoreCircle color="#4CAF50" />;
        }

        if (normalized === "no") {
          return <ScoreCircle color="#E53935" />;
        }
        return <span>-</span>;
      },
    }));
  const visibleSections = sectionDefinitions.filter((section) => {
    const visibilityKey = `section_summary_${section.key}`;
    return columnVisibility?.[visibilityKey] !== false;
  });

  const dynamicSectionColumns = visibleSections.map((section) => {
    const questions = questionSchema[section.key] || [];
    const hasQuestions = questions.length > 0;
    const isCurrentExpanded = expandedSection === section.key;

    if (isExpanded) {
      // Sections without questions get a single summary child so the group is
      // never empty. An empty columns:[] causes MUI to collapse the group and
      // shift all subsequent columns to the left.
      const groupColumns = hasQuestions
        ? buildQuestionColumns(questions, `section_answers_${section.key}`)
        : [
            {
              id: `${section.key}_summary_child`,
              header: "-",
              enableSorting: false,
              enableColumnFilter: false,
              size: 180,
              muiTableHeadCellProps: { align: "center", sx: { borderBottom: "1px solid #7A7A8C", borderLeft: "1px solid #C2C2CA" } },
              muiTableBodyCellProps: { align: "center", sx: { borderLeft: "1px solid #C2C2CA" } },
            },
          ];

      return {
        id: `${section.key}_group`,
        header: (
          <ExpandableHeader
            title={section.label}
            isExpanded
            onExpand={() => {}}
            onCollapse={() => setExpandedSection(null)}
            forceHover={headerHover === section.key}
          />
        ),
        muiTableHeadCellProps: {
          align: "center",
          sx: {
            ...headerHoverSx,
            borderLeft: "1px solid #BDBDBD",
          },
          onClick: () => setExpandedSection(null),
          onMouseEnter: () => setHeaderHover(section.key),
          onMouseLeave: () => setHeaderHover(null),
        },
        columns: groupColumns,
      };
    }

    return {
      id: `${section.key}_summary`,
      accessorKey: `section_summary_${section.key}`,
      header: (
        <ExpandableHeader
          title={section.label}
          isExpanded={false}
          onExpand={() => setExpandedSection(section.key)}
          onCollapse={() => {}}
          disabled={!hasQuestions}
          forceHover={headerHover === section.key}
        />
      ),
      size: 180,
      enableColumnFilter: false,
      enableSorting: false,
      grow: true,
      muiTableHeadCellProps: {
        align: "center",
        sx: headerHoverSx,
        onClick: () => {
          if (!hasQuestions) return;
          setExpandedSection(isCurrentExpanded ? null : section.key);
        },
        onMouseEnter: () => setHeaderHover(section.key),
        onMouseLeave: () => setHeaderHover(null),
      },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }: any) => {
        const value = cell.getValue();
        const sentiment = getScoreSentimentFromFraction(value);
        if (sentiment === "not_rated") return <span>-</span>;

        const color = sentiment === "positive" ? "#4CAF50" : "#E53935";
        return <ScoreCircle color={color} />;
      },
    };
  });

  return [
    ...baseColumns,
    ...dynamicSectionColumns,
  ];
};

export default getColumns;
